import React, {useMemo, useState, useEffect} from 'react';
import {Alert, Box, InputAdornment, Typography} from '@mui/material';
import PropTypes from 'prop-types';
import {validateEmail, validateFieldLength, validateMultipleEmails} from '../../../utility/validationUtil';
import AdmicityForm from '../../../shared-components/AdmicityForm';
import AdmicityCronScheduler from '../../../shared-components/AdmicityCronScheduler';
import LogoUploader from './LogoUploader';
import useUser from '../../../utility/hooks/useUser';
import {ROLES} from '../../../constants/roles';

const validateDFENumber = (value) => {
    const isValid = /^\d{3}\d{4}$/.test(value);
    return {
        isValid,
        message: isValid ? '' : 'DFE number must be in the format: 3 digits for authority and 4 digits for school number',
    };
};

const validateInterval = (value) => {
    const numericValue = +value;
    const isValid = Number.isInteger(numericValue) && numericValue >= 0 && numericValue <= 1000;

    return {
        isValid,
        message: isValid ? '' : 'The value must be a whole number between 0 and 1000. Enter 0 to disable.',
    };
};

const validateWebDomain = (value) => {
    const isValid = /^([a-zA-Z0-9][a-zA-Z0-9-_]*\.)*[a-zA-Z0-9]*[a-zA-Z0-9-_]*[a-zA-Z0-9]+$/.test(value) &&
        !value.includes('admicity.co.uk');

    return {
        isValid,
        message: isValid ? '' : 'Enter a valid domain (e.g., hogwarts) without ".admicity.co.uk"',
    };
};

const SchoolSetupForm = (
    {
        editMode,
        schoolInfo,
        onSubmit,
        isLoading,
        logo
    }) => {
    const [showAlert, setShowAlert] = useState(false);
    const [isFormValid, setIsFormValid] = useState(true);
    const [cronExpression, setCronExpression] = useState('');
    const [photoCronExpression, setPhotoCronExpression] = useState('');
    const {user} = useUser();

    useEffect(() => {
        const validateForm = () => {
            const fieldsToValidate = [
                { name: 'name', value: schoolInfo.name || '', required: true, validate: (value) => validateFieldLength(value, 400) },
                { name: 'dfeNumber', value: schoolInfo.dfeNumber || '', required: true, validate: validateDFENumber },
                { name: 'webDomain', value: schoolInfo.domain || '', required: true, validate: validateWebDomain },
                { name: 'emailAddresses', value: schoolInfo.emailAddresses || '', required: false, validate: validateMultipleEmails },
                { name: 'notes', value: schoolInfo.notes || '', required: false, validate: () => ({ isValid: true }) }
            ];

            const hasErrors = fieldsToValidate.some(field => {
                if (!field.required && (!field.value || field.value === '')) {
                    return false;
                }
                const validation = field.validate(field.value);
                return !validation.isValid;
            });

            setIsFormValid(!hasErrors);
        };

        validateForm();
    }, [schoolInfo]);

    const handleSubmit = (payload) => {
        const body =
            !editMode
                ? {
                    name: payload.name,
                    dfeNumber: payload.dfeNumber,
                    subDomain: payload.domain,
                    domain: process.env.REACT_APP_DOMAIN,
                    notes: payload.notes,
                    registrationInviteConfigOption: payload.registrationInviteConfigOption,
                    emailAddresses: payload.emailAddresses,
                    noAccountAutoReminderInterval: payload.noAccountAutoReminderInterval,
                    unconfirmedApplicationReminderIntervalInHours: payload.unconfirmedApplicationReminderIntervalInHours,
                    unconfirmedStudentReminderIntervalInHours: payload.unconfirmedStudentReminderIntervalInHours,
                    syncInterval: cronExpression,
                    syncPhotoInterval: photoCronExpression,
                    hideBoarderGroups: payload.hideBoarderGroups,
                    hideHouseGroups: payload.hideHouseGroups,
                    disableParentInvitations: payload.disableParentInvitations,
                    disableApplicationParentInvitations: payload.disableApplicationParentInvitations,
                    disableStudentInvitations: payload.disableStudentInvitations,
                    disableEmployeeInvitations: payload.disableEmployeeInvitations,
                    disableParentNotificationApplicationUpdates: payload.disableParentNotificationApplicationUpdates,
                    disableParentNotificationStudentUpdates: payload.disableParentNotificationStudentUpdates,
                    disableEnquiriesSync: payload.disableEnquiriesSync,
                    logo: payload.logo,
                    enquiryResponseEmail: payload.enquiryResponseEmail
                }
                : {
                    id: schoolInfo.id,
                    name: payload.name,
                    dfeNumber: payload.dfeNumber,
                    notes: payload.notes,
                    registrationInviteConfigOption: payload.registrationInviteConfigOption,
                    emailAddresses: payload.emailAddresses,
                    noAccountAutoReminderInterval: payload.noAccountAutoReminderInterval,
                    unconfirmedApplicationReminderIntervalInHours: payload.unconfirmedApplicationReminderIntervalInHours,
                    unconfirmedStudentReminderIntervalInHours: payload.unconfirmedStudentReminderIntervalInHours,
                    syncInterval: cronExpression,
                    syncPhotoInterval: photoCronExpression,
                    hideBoarderGroups: payload.hideBoarderGroups,
                    hideHouseGroups: payload.hideHouseGroups,
                    disableParentInvitations: payload.disableParentInvitations,
                    disableApplicationParentInvitations: payload.disableApplicationParentInvitations,
                    disableStudentInvitations: payload.disableStudentInvitations,
                    disableEmployeeInvitations: payload.disableEmployeeInvitations,
                    disableParentNotificationApplicationUpdates: payload.disableParentNotificationApplicationUpdates,
                    disableParentNotificationStudentUpdates: payload.disableParentNotificationStudentUpdates,
                    disableEnquiriesSync: payload.disableEnquiriesSync,
                    logo: payload.logo,
                    enquiryResponseEmail: payload.enquiryResponseEmail
                };

        setShowAlert(schoolInfo.hideBoarderGroups !== payload.hideBoarderGroups ||
            schoolInfo.hideHouseGroups !== payload.hideHouseGroups);

        onSubmit(body);
    };

    const textFields = useMemo(() => [
                {
                    label: 'School Name',
                    name: 'name',
                    initialValue: schoolInfo.name,
                    validate: (value) => validateFieldLength(value, 400)
                },
                {
                    label: 'DFE Number',
                    name: 'dfeNumber',
                    initialValue: schoolInfo.dfeNumber,
                    validate: validateDFENumber
                },
                {
                    label: 'School Web Domain at Admicity',
                    name: 'domain',
                    initialValue: schoolInfo.domain,
                    helperText: 'Enter a domain (e.g., hogwarts.admicity.co.uk)',
                    disabled: editMode,
                    InputProps: (!editMode
                        ? {
                            endAdornment: (
                                <InputAdornment position="end">
                                    <Typography>{process.env.REACT_APP_DOMAIN}</Typography>
                                </InputAdornment>
                            ),
                        }
                        : {}),
                    validate: validateWebDomain
                },
                {
                    label: 'Email addresses for profile change notifications (comma-separated)',
                    name: 'emailAddresses',
                    required: false,
                    initialValue: schoolInfo.emailAddresses,
                    validate: validateMultipleEmails,
                    helperText: 'Enter comma-separated email addresses (e.g., admin@school.com, notifications@school.com)'
                },
                {
                    label: 'Auto Reminder (hours) – Unregistered Contacts',
                    hint: 'Set to 0 to disable automatic reminders.',
                    name: 'noAccountAutoReminderInterval',
                    required: true,
                    initialValue: schoolInfo.noAccountAutoReminderInterval,
                    validate: validateInterval
                },
                {
                    label: 'Auto Reminder (hours) – Unconfirmed Applications',
                    hint: 'Set to 0 to disable automatic reminders.',
                    name: 'unconfirmedApplicationReminderIntervalInHours',
                    required: true,
                    initialValue: schoolInfo.unconfirmedApplicationReminderIntervalInHours,
                    validate: validateInterval
                },
                {
                    label: 'Auto Reminder (hours) – Unconfirmed Students',
                    hint: 'Set to 0 to disable automatic reminders.',
                    name: 'unconfirmedStudentReminderIntervalInHours',
                    required: true,
                    initialValue: schoolInfo.unconfirmedStudentReminderIntervalInHours,
                    validate: validateInterval
                },
        !schoolInfo.disableEnquiriesSync && {
                    label: 'Enquiry form notification email',
                    name: 'enquiryResponseEmail',
                    type: 'email',
                    initialValue: schoolInfo.enquiryResponseEmail,
                    required: false,
                    validate: validateEmail,
                    nullableOnEmpty: true
                },
                {
                    label: 'Notes',
                    name: 'notes',
                    initialValue: schoolInfo.notes,
                    multiline: true,
                    rows: 4,
                    required: false
                }
    ].filter(Boolean), [schoolInfo, editMode]);

    const options = useMemo(() =>
        [
            user.hasRole(ROLES.SUPER_ADMIN) && {
                label: 'Disable Enquiries Synchronization',
                name: 'disableEnquiriesSync',
                type: 'switch',
                initialValue: schoolInfo.disableEnquiriesSync
            },
            {
                label: 'Hide Boarder Groups',
                name: 'hideBoarderGroups',
                type: 'switch',
                initialValue: schoolInfo.hideBoarderGroups
            },
            {
                label: 'Hide House Groups',
                name: 'hideHouseGroups',
                type: 'switch',
                initialValue: schoolInfo.hideHouseGroups
            },
            {
                label: 'Disable Student Parent Invitations',
                name: 'disableParentInvitations',
                type: 'switch',
                initialValue: schoolInfo.disableParentInvitations
            },
            {
                label: 'Disable Application Parent Invitations',
                name: 'disableApplicationParentInvitations',
                type: 'switch',
                initialValue: schoolInfo.disableApplicationParentInvitations
            },
            {
                label: 'Disable Student Invitations',
                name: 'disableStudentInvitations',
                type: 'switch',
                initialValue: schoolInfo.disableStudentInvitations
            },
            {
                label: 'Disable Employee Invitations',
                name: 'disableEmployeeInvitations',
                type: 'switch',
                initialValue: schoolInfo.disableEmployeeInvitations
            },
            {
                label: 'Disable Parent Notifications for Application Profile Updates',
                name: 'disableParentNotificationApplicationUpdates',
                type: 'switch',
                initialValue: schoolInfo.disableParentNotificationApplicationUpdates
            },
            {
                label: 'Disable Parent Notifications for Student Profile Updates',
                name: 'disableParentNotificationStudentUpdates',
                type: 'switch',
                initialValue: schoolInfo.disableParentNotificationStudentUpdates
            }
        ].filter(Boolean), [schoolInfo]);

    const customFields = useMemo(() =>
        [
            {
                name: 'logo',
                initialValue: logo,
                render: props => <LogoUploader key="logo" {...props}/>
            }
        ], [logo]);

    const syncFields = useMemo(() =>
        [
            {
                name: 'syncInterval',
                initialValue: schoolInfo.syncInterval,
                render: (props) => {
                    // eslint-disable-next-line react/prop-types
                    const { onChange } = props;
                    return (
                        <AdmicityCronScheduler
                            key="syncInterval"
                            value={schoolInfo.syncInterval}
                            onChange={(cron) => {
                                setCronExpression(cron);
                                if (onChange) {
                                    onChange(cron);
                                }
                            }}
                            label="Sync"
                        />
                    );
                }
            },
            {
                name: 'syncPhotoInterval',
                initialValue: schoolInfo.syncPhotoInterval,
                render: (props) => {
                    // eslint-disable-next-line react/prop-types
                    const { onChange } = props;
                    return (
                        <AdmicityCronScheduler
                            key="syncPhotoInterval"
                            value={schoolInfo.syncPhotoInterval}
                            onChange={(cron) => {
                                setPhotoCronExpression(cron);
                                if (onChange) {
                                    onChange(cron);
                                }
                            }}
                            label="Photo Sync"
                        />
                    );
                }
            }
        ], [schoolInfo.syncInterval, schoolInfo.syncPhotoInterval]);

    return (
        <Box
            display="flex"
            flexDirection="column"
            sx={{
                mt: 2,
                width: {
                    xs: '100%',
                },
                maxWidth: 800,
                mx: 'auto',
                position: 'relative',
            }}
        >
            <Typography sx={{marginBottom: 2, fontWeight: 600}} variant="subtitle1">General School Settings</Typography>
            {
                showAlert
                    ? <Alert
                        sx={{marginBottom: 2}}
                        severity="info"
                        onClose={() => setShowAlert(false)}
                    >
                        Changes to the &quot;Hide House Groups&quot; or &quot;Hide Border Groups&quot; groups will take
                        effect during the next
                        synchronization, scheduled to occur in 1 hour.
                    </Alert>
                    : ''
            }
            <AdmicityForm
                textFields={textFields}
                options={options}
                custom={[
                    ...(user.hasRole(ROLES.SCHOOL_ADMIN) ? customFields : []),
                    ...syncFields
                ]}
                handleSubmit={handleSubmit}
                buttonText="Save"
                setIsChildFormValid={setIsFormValid}
                actionsContainerProps={{
                    container: {
                        justifyContent: 'flex-end'
                    },
                    button: {
                        fullWidth: false,
                        disabled: isLoading || !isFormValid
                    }
                }}
            />
        </Box>
    );
};

SchoolSetupForm.defaultProps = {
    editMode: false
};

SchoolSetupForm.propTypes = {
    editMode: PropTypes.bool,
    schoolInfo: PropTypes.object,
    logo: PropTypes.object,
    onSubmit: PropTypes.func,
    isLoading: PropTypes.bool
};

export default SchoolSetupForm;